import { useState } from "react";
import { Helmet } from "react-helmet";
import { DashboardLayout } from "../../../components/layouts/DashboardLayout";
import { Header } from "../../../components/Header";
import {
  FaQrcode,
  FaCreditCard,
  FaEye,
  FaEyeSlash,
  FaCopy,
  FaCheck,
  FaFloppyDisk,
} from "react-icons/fa6";

/* ============================================
   KOMPONEN: Toggle Switch
   ============================================ */
const ToggleSwitch = ({ defaultChecked = false, onChange }) => {
  const [checked, setChecked] = useState(defaultChecked);

  const handleToggle = () => {
    const newVal = !checked;
    setChecked(newVal);
    if (onChange) onChange(newVal);
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={handleToggle}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-300 focus:outline-none ${
        checked ? "bg-cyan-500" : "bg-slate-700"
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-300 ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </button>
  );
};

/* ============================================
   KOMPONEN: Api Key Field (Sesuai Gambar)
   ============================================ */
const ApiKeyField = ({ id, label, value, onChange, placeholder, isSecret = false }) => {
  const [showKey, setShowKey] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (value) {
      navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 py-3 border-b border-slate-700/30 last:border-b-0">
      {/* Label Kiri */}
      <label htmlFor={id} className="w-full sm:w-40 flex-shrink-0 text-xs font-semibold text-slate-400 uppercase tracking-wider">
        {label}
      </label>

      {/* Input & Tombol Kanan */}
      <div className="flex-1 flex gap-2">
        <div className="relative flex-1">
          <input
            id={id}
            type={isSecret && !showKey ? "password" : "text"}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            className="w-full px-4 py-2.5 text-sm rounded-lg bg-slate-800/80 border border-slate-600/30 text-gray-100 placeholder-slate-600 outline-none focus:border-cyan-500/50 transition-all font-mono pr-10"
          />
          {isSecret && (
            <button
              type="button"
              onClick={() => setShowKey(!showKey)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-cyan-400 transition-colors"
            >
              {showKey ? <FaEyeSlash className="text-sm" /> : <FaEye className="text-sm" />}
            </button>
          )}
        </div>

        {/* Tombol Salin */}
        <button
          type="button"
          onClick={handleCopy}
          className={`px-4 rounded-lg border text-xs font-semibold transition-all flex items-center gap-2 flex-shrink-0 ${
            copied 
              ? "border-green-500/30 bg-green-500/10 text-green-400" 
              : "border-slate-600/30 bg-slate-800/40 text-slate-400 hover:text-cyan-400 hover:border-cyan-500/30"
          }`}
        >
          {copied ? (
            <><FaCheck className="text-sm" /> Tersalin</>
          ) : (
            <><FaCopy className="text-sm" /> Salin</>
          )}
        </button>
      </div>
    </div>
  );
};

/* ============================================
   HALAMAN UTAMA: Payment Gateway
   ============================================ */
export const PaymentGatwayPage = () => {
  const [openSidebar, setOpenSidebar] = useState(false);

  // State Data Midtrans (Sesuai data di gambar)
  const [midtransConfig, setMidtransConfig] = useState({
    enabled: true,
    merchant_id: "G168551971",
    client_key: "Mid-client-CtsTfn95RBoEuBel",
    server_key: "Mid-server-Rh5tK9Ui5UUVKQVU24Glvqx",
  });

  const handleMidtransChange = (e) => {
    setMidtransConfig((prev) => ({ ...prev, [e.target.id]: e.target.value }));
  };

  const handleSave = () => {
    console.log("Midtrans Config Saved:", midtransConfig);
    alert("Konfigurasi Midtrans berhasil disimpan!");
  };

  return (
    <>
      <Helmet>
        <title>Payment Gateway - DANELA NET</title>
      </Helmet>

      <DashboardLayout showSidebar={openSidebar} setShowSidebar={setOpenSidebar}>
        {/* ===== Header ===== */}
        <Header toggleSidebar={() => setOpenSidebar(!openSidebar)}>
          <div className="flex flex-col">
            <h2 className="text-base md:text-xl font-display font-bold text-gray-100 tracking-tight">
              Payment Gateway
            </h2>
            <p className="text-xs text-slate-500 hidden md:block mt-0.5">
              Kelola kredensial API untuk metode pembayaran digital.
            </p>
          </div>
        </Header>

        {/* ===== Content ===== */}
        <main className="p-4 lg:p-6 max-w-4xl">
          <div className="space-y-6">
            
            {/* ------------------- CARD MIDTRANS SNAPSHOT ------------------- */}
            <article
              className="rounded-2xl overflow-hidden"
              style={{
                background: "rgba(15, 23, 42, 0.5)",
                border: "1px solid rgba(51, 65, 85, 0.3)",
                backdropFilter: "blur(12px)",
              }}
            >
              {/* Header Card */}
              <div
                className="flex items-center justify-between p-5"
                style={{ borderBottom: "1px solid rgba(51, 65, 85, 0.2)" }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-cyan-500/10 text-cyan-400 border border-cyan-500/20">
                    <FaCreditCard className="text-lg" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-100">Midtrans Payment</h3>
                    <p className="text-[11px] text-slate-500">VA, GoPay, ShopeePay, QRIS, dll</p>
                  </div>
                </div>
                <ToggleSwitch 
                  defaultChecked={midtransConfig.enabled} 
                  onChange={(val) => setMidtransConfig((prev) => ({...prev, enabled: val}))} 
                />
              </div>

              {/* Body Form Keys (Layout Gambar) */}
              {midtransConfig.enabled && (
                <div className="p-5 animate-in fade-in duration-300">
                  <div className="p-4 rounded-xl" style={{ background: "rgba(15, 23, 42, 0.4)", border: "1px solid rgba(51, 65, 85, 0.15)" }}>
                    
                    <ApiKeyField
                      id="merchant_id"
                      label="Merchant ID"
                      value={midtransConfig.merchant_id}
                      onChange={handleMidtransChange}
                      placeholder="GXXXXXXXXX"
                    />

                    <ApiKeyField
                      id="client_key"
                      label="Client Key"
                      value={midtransConfig.client_key}
                      onChange={handleMidtransChange}
                      placeholder="Mid-client-XXXXX"
                      isSecret={true}
                    />

                    <ApiKeyField
                      id="server_key"
                      label="Server Key"
                      value={midtransConfig.server_key}
                      onChange={handleMidtransChange}
                      placeholder="Mid-server-XXXXX"
                      isSecret={true}
                    />

                  </div>
                  
                  <p className="text-[11px] text-slate-600 mt-3 px-1">
                    * Pastikan Server Key tidak dibagikan ke publik. Gunakan environment <code className="text-cyan-600">.env</code> di backend Anda.
                  </p>
                </div>
              )}
            </article>

            {/* ------------------- FOOTER SAVE ------------------- */}
            <div className="flex justify-end pt-2">
              <button
                onClick={handleSave}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-bold text-white transition-all duration-200 hover:opacity-90"
                style={{
                  background: "linear-gradient(135deg, #0077B6, #00B4D8)",
                  boxShadow: "0 4px 15px rgba(0, 180, 216, 0.2)",
                }}
              >
                <FaFloppyDisk /> Simpan Konfigurasi
              </button>
            </div>

          </div>
        </main>
      </DashboardLayout>
    </>
  );
};